/**
 * useCalendarEvents Hook
 * Manages calendar events (DB-persisted via API) + calendar categories (localStorage).
 * Business calendar with custom category support.
 */
import { useState, useEffect, useCallback } from 'react';
import { calendarAPI } from '../services/api';

/* ──────────── Types ──────────── */
export interface CalendarCategory {
  id: string;
  name: string;
  color: string;
  visible: boolean;
  isDefault?: boolean;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  location?: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  color: string;
  allDay: boolean;
  categoryId: string;
  createdAt: string;
}

/* ──────────── Constants ──────────── */
const CATEGORIES_KEY = 'elvoaq_calendar_categories';

const generateTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const DEFAULT_CATEGORIES: CalendarCategory[] = [
  { id: 'meetings', name: 'Meetings', color: '#7c5cfc', visible: true, isDefault: true },
  { id: 'deadlines', name: 'Deadlines', color: '#ff4d6a', visible: true, isDefault: true },
  { id: 'reminders', name: 'Reminders', color: '#00d4aa', visible: true, isDefault: true },
  { id: 'reviews', name: 'Reviews', color: '#ff8c42', visible: true, isDefault: true },
  { id: 'presentations', name: 'Presentations', color: '#4da6ff', visible: true, isDefault: true },
  { id: 'training', name: 'Training', color: '#ff6eb4', visible: true, isDefault: true },
  { id: 'travel', name: 'Travel', color: '#ffd166', visible: true, isDefault: true },
  { id: 'holidays', name: 'Holidays', color: '#a8ff78', visible: true, isDefault: true },
];

/* ──────────── Mapper ──────────── */
const mapEvent = (raw: any): CalendarEvent => ({
  id: raw.id,
  title: raw.title,
  description: raw.description || undefined,
  location: raw.location || undefined,
  date: raw.date,
  startTime: raw.startTime || '00:00',
  endTime: raw.endTime || '23:59',
  color: raw.color || '#7c5cfc',
  allDay: Boolean(raw.allDay),
  categoryId: raw.categoryId || 'meetings',
  createdAt: raw.createdAt || new Date().toISOString(),
});

/* ──────────── Hook ──────────── */
export const useCalendarEvents = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [trashedEvents, setTrashedEvents] = useState<CalendarEvent[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [categories, setCategories] = useState<CalendarCategory[]>([]);
  const [loading, setLoading] = useState(true);

  // Load events from API + categories from localStorage
  useEffect(() => {
    const loadData = async () => {
      // Load categories from localStorage
      try {
        const storedCats = localStorage.getItem(CATEGORIES_KEY);
        if (storedCats) {
          setCategories(JSON.parse(storedCats));
        } else {
          setCategories(DEFAULT_CATEGORIES);
          localStorage.setItem(CATEGORIES_KEY, JSON.stringify(DEFAULT_CATEGORIES));
        }
      } catch {
        setCategories(DEFAULT_CATEGORIES);
      }

      // Load events from API
      try {
        const res = await calendarAPI.getEvents();
        setEvents((res.data || []).map(mapEvent));
      } catch (error) {
        console.error('Failed to load calendar events from API:', error);
        // Fallback: migrate from old localStorage if present
        try {
          const old = localStorage.getItem('elvoaq_calendar_events_v2');
          if (old) setEvents(JSON.parse(old));
        } catch {
          setEvents([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Persist categories to localStorage whenever they change
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(categories));
    }
  }, [categories, loading]);

  /* ──── Event CRUD ──── */
  const addEvent = useCallback(async (data: Omit<CalendarEvent, 'id' | 'createdAt'>) => {
    // Optimistic update
    const tempId = generateTempId();
    const optimistic: CalendarEvent = { ...data, id: tempId, createdAt: new Date().toISOString() };
    setEvents(prev => [...prev, optimistic]);
    try {
      const res = await calendarAPI.createEvent(data);
      const created = mapEvent(res.data);
      setEvents(prev => prev.map(e => (e.id === tempId ? created : e)));
      return created;
    } catch (error) {
      console.error('Failed to create event:', error);
      setEvents(prev => prev.filter(e => e.id !== tempId));
      throw error;
    }
  }, []);

  const updateEvent = useCallback(async (id: string, updates: Partial<Omit<CalendarEvent, 'id' | 'createdAt'>>) => {
    // Optimistic update
    setEvents(prev => prev.map(e => (e.id === id ? { ...e, ...updates } : e)));
    try {
      const res = await calendarAPI.updateEvent(id, updates);
      const updated = mapEvent(res.data);
      setEvents(prev => prev.map(e => (e.id === id ? updated : e)));
    } catch (error) {
      console.error('Failed to update event:', error);
      // Revert by reloading
      calendarAPI.getEvents().then(res => setEvents((res.data || []).map(mapEvent))).catch(() => {});
    }
  }, []);

  const deleteEvent = useCallback(async (id: string) => {
    setEvents(prev => prev.filter(e => e.id !== id));
    try {
      await calendarAPI.deleteEvent(id);
    } catch (error) {
      console.error('Failed to delete event:', error);
      // Revert
      calendarAPI.getEvents().then(res => setEvents((res.data || []).map(mapEvent))).catch(() => {});
    }
  }, []);

  const loadTrashedEvents = useCallback(async () => {
    setTrashLoading(true);
    try {
      const res = await calendarAPI.getTrashedEvents();
      setTrashedEvents((res.data || []).map(mapEvent));
    } catch (error) {
      console.error('Failed to load trashed events:', error);
    } finally {
      setTrashLoading(false);
    }
  }, []);

  const restoreEvent = useCallback(async (id: string) => {
    try {
      const res = await calendarAPI.restoreEvent(id);
      const restored = mapEvent(res.data);
      setTrashedEvents(prev => prev.filter(e => e.id !== id));
      setEvents(prev => [...prev, restored]);
    } catch (error) {
      console.error('Failed to restore event:', error);
    }
  }, []);

  const permanentDeleteEvent = useCallback(async (id: string) => {
    try {
      await calendarAPI.permanentDeleteEvent(id);
      setTrashedEvents(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      console.error('Failed to permanently delete event:', error);
    }
  }, []);

  const moveEvent = useCallback(async (id: string, newDate: string) => {
    setEvents(prev => prev.map(e => (e.id === id ? { ...e, date: newDate } : e)));
    try {
      await calendarAPI.moveEvent(id, newDate);
    } catch (error) {
      console.error('Failed to move event:', error);
      calendarAPI.getEvents().then(res => setEvents((res.data || []).map(mapEvent))).catch(() => {});
    }
  }, []);

  /* ──── Category CRUD (localStorage only) ──── */
  const addCategory = useCallback((name: string, color: string) => {
    const cat: CalendarCategory = { id: generateTempId(), name, color, visible: true, isDefault: false };
    setCategories(prev => [...prev, cat]);
    return cat;
  }, []);

  const updateCategory = useCallback((id: string, updates: Partial<Omit<CalendarCategory, 'id' | 'isDefault'>>) => {
    setCategories(prev => prev.map(c => (c.id === id ? { ...c, ...updates } : c)));
  }, []);

  const deleteCategory = useCallback((id: string) => {
    setCategories(prev => prev.filter(c => c.id !== id));
    // Move events from deleted category to 'meetings'
    setEvents(prev => prev.map(e => (e.categoryId === id ? { ...e, categoryId: 'meetings' } : e)));
  }, []);

  const toggleCategoryVisibility = useCallback((id: string) => {
    setCategories(prev => prev.map(c => (c.id === id ? { ...c, visible: !c.visible } : c)));
  }, []);

  /* ──── Queries ──── */
  const getVisibleEvents = useCallback(() => {
    const visibleIds = new Set(categories.filter(c => c.visible).map(c => c.id));
    return events.filter(e => visibleIds.has(e.categoryId));
  }, [events, categories]);

  const getEventsForDate = useCallback((dateStr: string) => {
    const visibleIds = new Set(categories.filter(c => c.visible).map(c => c.id));
    return events
      .filter(e => e.date === dateStr && visibleIds.has(e.categoryId))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [events, categories]);

  const searchEvents = useCallback((query: string) => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return events.filter(
      e => e.title.toLowerCase().includes(q) ||
           e.location?.toLowerCase().includes(q) ||
           e.description?.toLowerCase().includes(q)
    );
  }, [events]);

  const getCategoryById = useCallback((id: string) => {
    return categories.find(c => c.id === id);
  }, [categories]);

  return {
    events,
    trashedEvents,
    trashLoading,
    categories,
    loading,
    addEvent,
    updateEvent,
    deleteEvent,
    loadTrashedEvents,
    restoreEvent,
    permanentDeleteEvent,
    moveEvent,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryVisibility,
    getVisibleEvents,
    getEventsForDate,
    searchEvents,
    getCategoryById,
  };
};
